import { useRef } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import type { CredentialType } from "@api/shared-runtime/enums";
import { supportsCredentialImportFieldSelection } from "@api/shared-runtime/credential-import-fields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";
import { CredentialDocumentPreviewModal } from "../credential-card/credential-document-preview-modal";
import { useProviderImportReview } from "../../hooks/use-provider-import-review";
import { ProviderOfferList } from "./provider-offer-list";
import { ProviderOfferReview } from "./provider-offer-review";
import { ProviderImportConfirmation } from "./provider-import-confirmation";
import type {
  ProviderImportScope,
  ProviderOfferList as OfferListData,
} from "./provider-import-types";

export function ProviderImportDialog({
  scope,
  data,
  canImport,
  initialOfferId,
  loading,
  loadError,
  onClose,
}: {
  scope: ProviderImportScope;
  data: OfferListData | undefined;
  canImport: boolean;
  initialOfferId: string | null;
  loading: boolean;
  loadError: string | null;
  onClose: () => void;
}) {
  const openerRef = useRef<HTMLElement | null>(null);
  const {
    transport,
    filter,
    setFilter,
    offers,
    setFieldSelected,
    activeId,
    active,
    fetching,
    busy,
    error,
    progress,
    confirmation,
    reviewOffer,
    backToList,
    refreshList,
    importOffer,
    dismissOffer,
    requestBulk,
    confirmBulk,
    cancelBulk,
  } = useProviderImportReview({ scope, data, canImport, initialOfferId });
  let title = "Import from provider";
  if (active) {
    title = `${active.label}${active.identityLabel ? ` · ${active.identityLabel}` : ""}`;
  }
  const displayError = error || loadError;
  const canConfirmSelection = Boolean(
    activeId && active?.isActionable && canImport && !loading && !displayError,
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy && !confirmation && !transport.preview) {
          onClose();
        }
      }}
    >
      <DialogContent
        onOpenAutoFocus={() => {
          if (document.activeElement instanceof HTMLElement) {
            openerRef.current = document.activeElement;
          }
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (openerRef.current?.isConnected) {
            openerRef.current.focus();
          } else {
            Array.from(
              document.querySelectorAll<HTMLElement>(
                '[role="dialog"][data-state="open"]',
              ),
            )
              .at(-1)
              ?.focus();
          }
        }}
        className={cn(
          PROFILE_FORM_STYLES.dialog,
          "sm:[&.dialog-content]:max-w-4xl",
        )}
      >
        <DialogHeader className={PROFILE_FORM_STYLES.header}>
          <DialogTitle className={PROFILE_FORM_STYLES.title}>
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Provider credential changes
          </DialogDescription>
        </DialogHeader>
        <div className={PROFILE_FORM_STYLES.body}>
          {displayError && (
            <div role="alert" className={PROFILE_FORM_STYLES.error}>
              <p>{displayError}</p>
              <Button
                variant="link"
                className="mt-2 h-auto p-0"
                disabled={busy}
                onClick={() => {
                  if (activeId) {
                    void reviewOffer(activeId);
                  } else {
                    void refreshList();
                  }
                }}
              >
                Refresh
              </Button>
            </div>
          )}
          {(loading || fetching) && (
            <p role="status" className="text-sm text-slate-600">
              Loading provider credentials…
            </p>
          )}
          {!fetching && active && (
            <ProviderOfferReview
              offer={active}
              canSelectFields={
                canImport &&
                active.isActionable &&
                supportsCredentialImportFieldSelection(
                  active.credentialType as CredentialType,
                )
              }
              disabled={busy}
              onFieldChange={setFieldSelected}
              onPreview={(input) =>
                void transport.openPreview({ ...input, offerId: active.id })
              }
            />
          )}
          {!activeId && data && (
            <ProviderOfferList
              offers={offers}
              filter={filter}
              onFilter={setFilter}
              canImport={canImport}
              disabled={busy}
              progress={progress}
              onReview={(id) => void reviewOffer(id)}
              onImport={(offer) => void importOffer(offer)}
              onDismiss={(offer) => void dismissOffer(offer)}
              onBulk={requestBulk}
            />
          )}
        </div>
        <DialogFooter
          className={cn(
            PROFILE_FORM_STYLES.footer,
            canConfirmSelection ? "sm:justify-end" : "sm:justify-start",
          )}
        >
          {canConfirmSelection && (
            <Button
              type="button"
              className="gap-2"
              disabled={busy}
              onClick={() => void backToList()}
            >
              <Check className="size-4" aria-hidden="true" />
              Confirm selection
            </Button>
          )}
          {activeId && !canConfirmSelection && (
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={busy}
              onClick={() => void backToList()}
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to shared credentials
            </Button>
          )}
          {!activeId && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={onClose}
            >
              Done
            </Button>
          )}
        </DialogFooter>
        {confirmation && (
          <ProviderImportConfirmation
            confirmation={confirmation}
            progress={progress}
            onCancel={cancelBulk}
            onConfirm={() => void confirmBulk()}
          />
        )}
        {transport.preview && (
          <CredentialDocumentPreviewModal
            document={transport.preview.document}
            url={transport.preview.url}
            error={transport.preview.error}
            isLoading={transport.isPreviewLoading}
            onDownload={() =>
              void transport
                .download()
                .catch((cause: unknown) =>
                  toast.error(
                    cause instanceof Error
                      ? cause.message
                      : "Could not download document.",
                  ),
                )
            }
            onClose={transport.closePreview}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
